import { Box } from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { useCallback, useRef } from 'react';
import {
  ButtonContainer,
  IconLibrary,
  PermissionButton,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { FormattedMessage } from 'react-intl';
import {
  preProcessResponseData,
  fetchClaimsSetting,
  fetchRateCard,
  updateClaimsSettingApi,
  updateRateCard,
} from './lib';
import validationSchema from './validations';

function SystemSettingClaims({
  schema,
  onChange = () => undefined,
  isLocationScope,
  formId,
  permission,
  config,
}) {
  const formRef = useRef();
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, put } = useDataProvider(provider);

  const { claimSettingsApiUrl: claimApiUrl, rateCardApiUrl: rateCardUrl } =
    config.api;

  const commonParams = {
    claimApiUrl,
    rateCardUrl,
    get,
    put,
    isLocationScope,
  };

  const fetchDefaultValues = useCallback(async () => {
    const [claimData, rateCardData] = await Promise.all([
      fetchClaimsSetting(commonParams),
      fetchRateCard(commonParams),
    ]);
    const initialValuesFound = preProcessResponseData(claimData, rateCardData);
    const result = {
      data: initialValuesFound,
      errors: null,
    };

    return result;
  }, [claimApiUrl, rateCardUrl, get, put]);

  const onSubmit = async data => {
    let errors = [];
    let claimData = null;
    let rateCardData = null;

    const [claimResponse, rateCardResponse] = await Promise.all([
      updateClaimsSettingApi(commonParams, data),
      updateRateCard(commonParams, data),
    ]);

    if (claimResponse?.ok) {
      claimData = claimResponse?.data;
    } else {
      errors = [...(claimResponse?.errors || [])];
    }

    if (rateCardResponse?.ok) {
      rateCardData = rateCardResponse?.data;
    } else {
      errors = [...(rateCardResponse?.errors || [])];
    }

    if (errors.length > 0) {
      return {
        nextValues: data,
        errors,
      };
    }
    snackbar.showMessage({
      type: 'success',
      data: formatMessage({
        id: 'CLAIM_SYSTEM_SETTINGS_WERE_SAVED_SUCCESSFULLY',
        defaultMessage: 'Claim System settings were saved successfully',
      }),
    });
    const processedValues = preProcessResponseData(claimData, rateCardData);
    return {
      nextValues: processedValues,
      errors: null,
      callback: () => {
        onChange();
      },
    };
  };
  return (
    <Box>
      <Form
        formId={formId}
        ref={formRef}
        permissionEntities={[permission]}
        onSubmit={onSubmit}
        schema={schema}
        fetchDefaultValues={fetchDefaultValues}
        validationSchema={validationSchema(formatMessage)}>
        <ButtonContainer>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_CLAIMS"
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId })}>
            <FormattedMessage
              id="SAVE_CLAIM_SETTINGS"
              defaultMessage="Save Claim Settings"
            />
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_CLAIMS"
            variant="contained"
            startIcon={<IconLibrary.Reset />}
            color="primary"
            onClick={() => formRef.current?.reset()}>
            <FormattedMessage id="RESET" defaultMessage="Reset" />
          </PermissionButton>
        </ButtonContainer>
      </Form>
    </Box>
  );
}

export default SystemSettingClaims;
